// src/pages/administrativo/ConfigTEFCad.js
import React, { useState, useEffect } from 'react';

import Input from '../../components/input/Input.js';
import Select from '../../components/input/Select.js';
import { useConfigTEF } from '../../contexts/administrativo/ConfigTEFContext.js';
import ConfigTEFSearch from './ConfigTEFSearch';
import { ConfigTEF, IntegradorTEFEnum } from '../../models/administrativo/ConfigTEF.ts'; // Assumindo que existe este modelo
import { useMessage } from '../../contexts/geral/MessageContext.js';

import { useYesNo } from '../../contexts/geral/YesNoContext.js';
import ModalContainer from '../../components/modalForm/ModalContainer';
import ModalHeader from '../../components/modalForm/ModalHeader';
import ModalBody from '../../components/modalForm/ModalBody';
import ModalFooter from '../../components/modalForm/ModalFooter';
import ModalActions, { ModalActionButton } from '../../components/modalForm/ModalActions';

const ConfigTEFCad = ({ show, onHide }) => {
    const { configTEFList, addConfigTEF, updateConfigTEF, deleteConfigTEF } = useConfigTEF();
    const [configTEF, setConfigTEF] = useState(new ConfigTEF());
    const [showSearchModal, setShowSearchModal] = useState(false);
    const {showMessage} = useMessage();
    const { showYesNo } = useYesNo();

    const [novoReg, setNovoReg] = useState(false);

    useEffect(() => {
        setConfigTEF(new ConfigTEF());
    }, []);

    const handleNovo = () => {
        setConfigTEF(new ConfigTEF());
        setNovoReg(true);
    };

    const handleEditar = (id) => {
        if (id === 0) {
            showMessage("Selecione uma configuração de TEF para editar!");            
            return;
        }
        setNovoReg(false);
    };

    const handleDelete = async () => {
        if (!configTEF?.id) {
            showMessage("Selecione uma configuração de TEF para excluir!");        
        } else {
            const resposta = await showYesNo("Deseja excluir a configuração de TEF selecionada?");
            if (resposta) {
                await deleteConfigTEF(configTEF.id);        
                handleNovo();
            }
        }
    };

    const handleSave = async () => {
        try {
            if (!configTEF.nomeTerminal) { // Ajuste o campo obrigatório conforme o modelo
                showMessage("Nome do terminal não preenchido!");                
                return;
            }

            if (Number(configTEF.integrador) === IntegradorTEFEnum.SuperTef && !configTEF.posId) {
                showMessage("Informe o Id POS para o terminal SuperTEF!", "warning");
                return;
            }

            if (!configTEF.id) {
                await addConfigTEF(configTEF);
            } else {
                await updateConfigTEF(configTEF);
            }
            handleNovo();
        } catch (error) {
            console.error("Erro ao gravar dados: ", error);
        }
    };

    const handleExit = () => {
        const configSelecionada = configTEFList.find(c => c.id.toString() === configTEF?.id?.toString());

        if (configSelecionada) {
            setConfigTEF(configSelecionada);
            handleEditar(configTEF?.id);
        } else {
            setConfigTEF(new ConfigTEF());
        }
    };

    if (!show) return null;

    return (
        <ModalContainer show={show} largura="600px" variant="cadastro" onHide={onHide}>

            <ModalHeader title={novoReg ? 'Nova Configuração de TEF' : 'Alterar Configuração de TEF'} onHide={onHide} />
            <ModalBody className="modal-body">
                <form>
                    <div className="input-group mb-3">
                        <Input
                            label="Código"
                            value={configTEF.id || ''}
                            onChange={(e) => setConfigTEF({ ...configTEF, id: e.target.value })}
                            onBlur={handleExit}
                            disabled={!novoReg}
                            btnBuscar={() => setShowSearchModal(true)}
                        />
                        <Input
                            label="Nome Terminal"
                            value={configTEF.nomeTerminal || ''} // Ajuste conforme o modelo
                            onChange={(e) => setConfigTEF({ ...configTEF, nomeTerminal: e.target.value })}
                            size={300}
                            autoFocus={true}
                        />
                    </div>
                    {/* Campos adicionais para ConfigTEF */}
                    <div className="input-group">
                        <Input
                            label="CNPJ Credenciadora"
                            value={configTEF.cnpjCreden || ''} // Campo fictício, ajuste conforme necessário
                            onChange={(e) => setConfigTEF({ ...configTEF, cnpjCreden: e.target.value })}
                            size={170}
                            mask={'99.999.999/9999-99'}
                        />
                    </div>
                    <div className="input-group">
                        <Input
                            label="Serial ID Terminal"
                            value={configTEF.numSerialPOS || ''} // Campo fictício, ajuste conforme necessário
                            onChange={(e) => setConfigTEF({ ...configTEF, numSerialPOS: e.target.value })}
                            size={150}
                        />
                        <Input
                            label="IP e Porta Serial"
                            value={configTEF.ipTerminal || ''} // Campo fictício, ajuste conforme necessário
                            onChange={(e) => setConfigTEF({ ...configTEF, ipTerminal: e.target.value })}
                            size={150}
                        />
                    </div>
                    <div className="input-group">
                        <Input
                            label="Filial"
                            value={configTEF.filialId || ''} // Mantido similar ao ConfigDFe
                            onChange={(e) => setConfigTEF({ ...configTEF, filialId: e.target.value })}
                            size={100}
                        />
                        <Input
                            label="Id POS"
                            value={configTEF.posId || ''} // Mantido similar ao ConfigDFe
                            onChange={(e) => setConfigTEF({ ...configTEF, posId: e.target.value })}
                            size={100}
                        />
                        <Select
                            label="Integrador"
                            value={configTEF.integrador ?? IntegradorTEFEnum.Nenhum}
                            onChange={(e) => setConfigTEF({ ...configTEF, integrador: Number(e.target.value) })}
                            size={150}
                            options={[
                                { value: IntegradorTEFEnum.Nenhum, label: 'Nenhum (sem TEF)' },
                                { value: IntegradorTEFEnum.MSiTef, label: 'm-SiTef' },
                                { value: IntegradorTEFEnum.SuperTef, label: 'SuperTEF' },
                                { value: IntegradorTEFEnum.Cielo, label: 'Cielo' },
                                { value: IntegradorTEFEnum.Banrisul, label: 'Banrisul' },
                                { value: IntegradorTEFEnum.Manual, label: 'TEF Manual' }
                            ]}
                        />
                    </div>
                </form>
            </ModalBody>
            <ModalFooter className="modal-footer justify-content-end">
                <ModalActions >
                    <ModalActionButton className="btn btn-primary" onClick={handleSave} action="save">
                        Salvar
                    </ModalActionButton>
                    <ModalActionButton className="btn btn-danger" onClick={handleDelete} disabled={novoReg} action="delete">
                        Excluir
                    </ModalActionButton>
                    <ModalActionButton className="btn btn-secondary" onClick={handleNovo} disabled={novoReg} action="new">
                        Novo
                    </ModalActionButton>
                </ModalActions>
            </ModalFooter>

            <ConfigTEFSearch
                show={showSearchModal}
                onHide={() => setShowSearchModal(false)}
                onSelectConfigTEF={(configSel) => {
                    setConfigTEF(configSel);
                    setShowSearchModal(false);
                    handleEditar(configSel.id);
                }}
            />

        </ModalContainer>
    );
};

export default ConfigTEFCad;
